import React from "react";
import LegalPage from "../components/LegalPage";

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 marker:text-[#1d4ed8]/60">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

const H3 = ({ children }) => (
  <h3 className="text-[17px] font-semibold text-slate-900 mt-4">{children}</h3>
);

const ProviderCard = ({ name, purpose, bullets, footer }) => (
  <div className="rounded-xl bg-white border border-slate-100 p-4">
    <p className="font-semibold text-slate-900">{name}</p>
    {purpose && <p className="text-slate-600 mt-1">{purpose}</p>}
    {bullets && (
      <ul className="mt-2 list-disc pl-6 space-y-1 text-slate-600 marker:text-[#1d4ed8]/60">
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    )}
    {footer && <p className="text-slate-500 text-[13.5px] mt-2">{footer}</p>}
  </div>
);

const SECTIONS = [
  {
    id: "purpose",
    heading: "1. Purpose",
    body: [
      <p key="1a">
        This Cookie Policy explains how Medily Ltd (“Medily”, “we”, “our”, or “us”) uses cookies
        and similar technologies when you visit our website, create an account, access our
        platform, or use any of our services.
      </p>,
      <p key="1b">This policy should be read alongside our:</p>,
      <List key="1c" items={["Privacy Policy", "Terms & Conditions", "Refund & Cancellation Policy"]} />,
    ],
  },
  {
    id: "what-are-cookies",
    heading: "2. What Are Cookies?",
    body: [
      <p key="2a">
        Cookies are small text files placed on your computer, tablet or mobile device when you
        visit a website.
      </p>,
      <p key="2b">Cookies allow websites to:</p>,
      <List
        key="2c"
        items={[
          "remember your preferences;",
          "maintain secure login sessions;",
          "improve website performance;",
          "analyse how visitors use the website;",
          "personalise certain content; and",
          "provide a safer and more reliable user experience.",
        ]}
      />,
      <p key="2d">Cookies cannot access files stored on your device or install software.</p>,
    ],
  },
  {
    id: "why-we-use-cookies",
    heading: "3. Why We Use Cookies",
    body: [
      <p key="3a">Medily uses cookies to:</p>,
      <List
        key="3b"
        items={[
          "provide secure access to user accounts;",
          "keep users logged into their account;",
          "remember cookie preferences;",
          "process subscriptions and payments securely;",
          "improve website functionality;",
          "understand how visitors use the website;",
          "monitor website performance;",
          "detect fraud and protect accounts;",
          "improve our products and services.",
        ]}
      />,
      <p key="3c" className="font-medium text-slate-900">
        We do not use cookies to sell personal information.
      </p>,
    ],
  },
  {
    id: "types-of-cookies-we-use",
    heading: "4. Types of Cookies We Use",
    body: [
      <H3 key="4.1h">4.1 Strictly Necessary Cookies</H3>,
      <p key="4.1a">These cookies are essential for the operation of our website.</p>,
      <p key="4.1b">Without them, services such as:</p>,
      <List
        key="4.1l1"
        items={[
          "logging in,",
          "purchasing subscriptions,",
          "accessing Resources,",
          "accessing Maya,",
          "maintaining secure sessions,",
        ]}
      />,
      <p key="4.1c">would not function correctly.</p>,
      <p key="4.1d">Examples include:</p>,
      <List
        key="4.1l2"
        items={["authentication cookies", "session cookies", "security cookies", "payment security cookies"]}
      />,
      <p key="4.1e">These cookies do not require consent under applicable law.</p>,

      <H3 key="4.2h">4.2 Functional Cookies</H3>,
      <p key="4.2a">Functional cookies remember choices you make to improve your experience.</p>,
      <p key="4.2b">These may include:</p>,
      <List
        key="4.2l"
        items={[
          "preferred theme",
          "cookie preferences",
          "remembered login details (where selected)",
          "language preferences",
          "accessibility preferences",
        ]}
      />,
      <p key="4.2c">These cookies improve convenience but are not essential.</p>,

      <H3 key="4.3h">4.3 Analytics Cookies</H3>,
      <p key="4.3a">Analytics cookies help us understand how visitors interact with our website.</p>,
      <p key="4.3b">For example, they may collect information about:</p>,
      <List
        key="4.3l1"
        items={[
          "pages visited",
          "time spent on pages",
          "navigation paths",
          "device type",
          "browser type",
          "approximate location",
          "referral source",
        ]}
      />,
      <p key="4.3c">This information is aggregated and does not normally identify individual users.</p>,
      <p key="4.3d">Analytics cookies help us improve:</p>,
      <List
        key="4.3l2"
        items={["website usability", "navigation", "performance", "content", "user experience"]}
      />,
      <p key="4.3e">
        Where required by law, analytics cookies will only be used after obtaining your consent.
      </p>,

      <H3 key="4.4h">4.4 Performance Cookies</H3>,
      <p key="4.4a">
        Performance cookies help us monitor website speed, stability and technical performance.
      </p>,
      <p key="4.4b">These cookies allow us to:</p>,
      <List
        key="4.4l"
        items={["identify broken pages", "monitor loading times", "improve reliability", "detect technical issues"]}
      />,

      <H3 key="4.5h">4.5 Marketing Cookies</H3>,
      <p key="4.5a">At present, Medily does not use advertising or behavioural marketing cookies.</p>,
      <p key="4.5b">
        If this changes in the future, we will update this Cookie Policy and request your consent
        before such cookies are placed.
      </p>,
    ],
  },
  {
    id: "third-party-cookies",
    heading: "5. Third-Party Cookies",
    body: [
      <p key="5a">Certain third-party providers may place cookies when you use our services.</p>,
      <p key="5b">These providers may include:</p>,
      <div key="5-list" className="space-y-3">
        <ProviderCard
          name="Stripe"
          purpose="Used to process secure payments."
          bullets={[
            "prevent fraud",
            "verify payment sessions",
            "maintain secure checkout",
            "detect suspicious activity",
          ]}
          footer="Further information is available in Stripe’s own Privacy and Cookie documentation."
        />
        <ProviderCard
          name="Calendly"
          purpose="When booking a University-Specific Mock MMI, Calendly may place cookies necessary to:"
          bullets={[
            "display booking availability",
            "remember scheduling preferences",
            "complete bookings securely",
          ]}
          footer="Calendly’s cookies are governed by Calendly’s own policies."
        />
        <ProviderCard
          name="Google Analytics (if enabled)"
          purpose="Google Analytics may use cookies to help us understand:"
          bullets={["visitor numbers", "website performance", "page popularity", "user behaviour"]}
          footer="Google Analytics data is used in aggregate and is not intended to identify individual users. Where required by law, Google Analytics cookies will only be enabled after consent has been given."
        />
        <ProviderCard
          name="Authentication Providers"
          purpose="If users choose to sign in using third-party authentication providers in the future (such as Google), those providers may place their own cookies according to their respective privacy policies."
        />
      </div>,
    ],
  },
  {
    id: "cookie-consent",
    heading: "6. Cookie Consent",
    body: [
      <p key="6a">When you first visit the Medily website, you will be presented with a Cookie Banner.</p>,
      <p key="6b">You may choose to:</p>,
      <List key="6c" items={["Accept All Cookies", "Reject Non-Essential Cookies", "Manage Preferences"]} />,
      <p key="6d">Your choices will be remembered using a cookie preference cookie.</p>,
      <p key="6e">
        You may change your preferences at any time using the Cookie Settings link available on
        the website.
      </p>,
    ],
  },
  {
    id: "managing-cookies",
    heading: "7. Managing Cookies",
    body: [
      <p key="7a">You may also manage cookies through your web browser.</p>,
      <p key="7b">Most browsers allow you to:</p>,
      <List
        key="7c"
        items={[
          "delete cookies;",
          "block cookies;",
          "restrict cookies;",
          "receive notifications before cookies are stored.",
        ]}
      />,
      <p key="7d">Please note that disabling certain cookies may affect website functionality.</p>,
      <p key="7e">For example:</p>,
      <List
        key="7f"
        items={[
          "you may need to log in repeatedly;",
          "some features may not work correctly;",
          "personalised settings may not be remembered.",
        ]}
      />,
    ],
  },
  {
    id: "cookie-retention",
    heading: "8. Cookie Retention",
    body: [
      <p key="8a">Different cookies remain on your device for different periods.</p>,
      <H3 key="8s1">Session Cookies</H3>,
      <p key="8s1p">These expire automatically when your browser is closed.</p>,
      <H3 key="8s2">Persistent Cookies</H3>,
      <p key="8s2p">These remain until:</p>,
      <List
        key="8s2l"
        items={["they expire automatically;", "you delete them; or", "you clear your browser cookies."]}
      />,
      <p key="8s2e">Retention periods depend on the purpose of each cookie.</p>,
    ],
  },
  {
    id: "example-cookies-used-by-medily",
    heading: "9. Example Cookies Used by Medily",
    body: [
      <div key="9-table" className="overflow-x-auto rounded-xl border border-slate-100">
        <table className="w-full text-[14.5px]">
          <thead className="bg-[#f4f7fd] text-slate-600">
            <tr>
              <th className="text-left font-semibold px-4 py-2.5">Cookie Category</th>
              <th className="text-left font-semibold px-4 py-2.5">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              ["Authentication", "Keeps users securely logged in"],
              ["Session", "Maintains secure browsing sessions"],
              ["Cookie Preferences", "Remembers cookie choices"],
              ["Functional", "Stores user preferences"],
              ["Analytics", "Understands website usage (where consent given)"],
              ["Performance", "Monitors website reliability"],
              ["Payment", "Supports secure Stripe transactions"],
              ["Booking", "Supports Calendly booking sessions"],
            ].map(([c, p]) => (
              <tr key={c}>
                <td className="px-4 py-2.5 text-slate-800">{c}</td>
                <td className="px-4 py-2.5 text-slate-600">{p}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
      <p key="9-note">This table is illustrative and may change as the website develops.</p>,
    ],
  },
  {
    id: "changes-to-this-policy",
    heading: "10. Changes to This Policy",
    body: [
      <p key="10a">We may update this Cookie Policy from time to time to reflect:</p>,
      <List
        key="10b"
        items={[
          "changes to our services;",
          "changes in technology;",
          "legal or regulatory requirements;",
          "new integrations;",
          "improvements to our platform.",
        ]}
      />,
      <p key="10c">The latest version will always be published on our website.</p>,
    ],
  },
  {
    id: "contact",
    heading: "11. Contact",
    body: [
      "If you have any questions about this Cookie Policy, please contact:",
      "Medily Ltd",
      "Email: info@medily.co",
    ],
  },
  {
    id: "your-rights",
    heading: "12. Your Rights",
    body: [
      <p key="12a">
        Depending on your location and applicable law, you may have rights regarding the use of
        cookies and your personal data.
      </p>,
      <p key="12b">These rights may include:</p>,
      <List
        key="12c"
        items={[
          "withdrawing consent;",
          "requesting access to personal data;",
          "requesting correction of inaccurate data;",
          "requesting deletion of personal data;",
          "restricting certain processing;",
          "objecting to processing in certain circumstances.",
        ]}
      />,
      <p key="12d">Further information is available in our Privacy Policy.</p>,
    ],
  },
  {
    id: "compliance",
    heading: "13. Compliance",
    body: [
      <p key="13a">This Cookie Policy is intended to support Medily’s compliance with:</p>,
      <List
        key="13b"
        items={[
          "UK General Data Protection Regulation (UK GDPR)",
          "Data Protection Act 2018",
          "Privacy and Electronic Communications Regulations (PECR)",
          "other applicable UK data protection legislation",
        ]}
      />,
      <p key="13c">
        Nothing in this Cookie Policy limits any statutory rights available to users under
        applicable law.
      </p>,
    ],
  },
];

const CookiePolicy = () => (
  <LegalPage
    title="Cookie Policy"
    subtitle="How Medily uses cookies and similar technologies when you visit our website, create an account, access our platform or use any of our services."
    sections={SECTIONS}
  />
);

export default CookiePolicy;
